import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    LinearProgress,
    Divider
} from '@mui/material';
import { VacancyStats, RecruitmentStep } from '../types/jobVacancy';

interface VacancyStatsCardProps {
    stats: VacancyStats;
}

const stepLabels: Record<RecruitmentStep, string> = {
    [RecruitmentStep.RESUME_SCREENING]: 'Triagem de Currículo',
    [RecruitmentStep.BEHAVIORAL_EVALUATION]: 'Avaliação Comportamental',
    [RecruitmentStep.TECHNICAL_TEST]: 'Teste Técnico',
    [RecruitmentStep.RESUME_AND_TEST_EVALUATION]: 'Avaliação de Currículo e Teste',
    [RecruitmentStep.TECHNICAL_INTERVIEW]: 'Entrevista Técnica',
    [RecruitmentStep.NEGOTIATION]: 'Negociação',
    [RecruitmentStep.CLOSED]: 'Fechado',
    [RecruitmentStep.REJECTED]: 'Rejeitado'
};

export const VacancyStatsCard: React.FC<VacancyStatsCardProps> = ({ stats }) => {
    const totalApplications = Object.values(stats.applications_by_step).reduce((a, b) => a + b, 0);

    return (
        <Card>
            <CardContent>
                <Grid container spacing={3}>
                    {/* Métricas Gerais */}
                    <Grid item xs={12} md={4}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Métricas Gerais
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Vagas
                                </Typography>
                                <Typography variant="h4">
                                    {stats.total_vacancies}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Vagas Abertas
                                </Typography>
                                <Typography variant="h4">
                                    {stats.open_vacancies}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Tempo Médio de Preenchimento
                                </Typography>
                                <Typography variant="h4">
                                    {stats.avg_time_to_fill} dias
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Candidatos por Etapa */}
                    <Grid item xs={12} md={4}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Candidatos por Etapa
                            </Typography>
                            {Object.entries(stats.applications_by_step).map(([step, count]) => (
                                <Box key={step} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2">
                                            {stepLabels[step as RecruitmentStep]}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {count} ({totalApplications > 0 ? Math.round((count / totalApplications) * 100) : 0}%)
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={totalApplications > 0 ? (count / totalApplications) * 100 : 0}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Taxas de Conversão */}
                    <Grid item xs={12} md={4}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Taxas de Conversão
                            </Typography>
                            {stats.conversion_rates.map((rate, index) => (
                                <Box key={rate.step} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2">
                                            {stepLabels[rate.step as RecruitmentStep]}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {rate.conversion_rate.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={rate.conversion_rate}
                                        color={index === stats.conversion_rates.length - 1 ? 'success' : 'primary'}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}; 